import React, { useState } from 'react'
import Swal from 'sweetalert2'
import { useBarista } from '../contexts/BaristaContext'
import { autoRotateCashDrawer } from '../utils/cashDrawerHandoff'

import { useNetworkStatus } from '../offline/useNetworkStatus'
import { offlineDB } from '../offline/db'
import bcrypt from 'bcryptjs'

export default function BaristaLoginModal({ onSuccess, onCancel, allowCancel = true, presetBarista = null, branchId, rotateCashDrawer = false, }) {    const { branchBaristas, baristasLoading, verifyBaristaPin, setupBaristaPin, setSelectedBarista, selectedBarista } = useBarista()

    const [step, setStep] = useState(presetBarista ? (presetBarista.has_pin ? 'pin' : 'setup') : 'pick')
    const [pickedBarista, setPickedBarista] = useState(presetBarista)
    const [pin, setPin] = useState('')
    const [confirmPin, setConfirmPin] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const isOnline = useNetworkStatus()

    const handlePick = (barista) => {
        setPickedBarista(barista)
        setPin('')
        setConfirmPin('')
        setError('')
        setStep(barista.has_pin ? 'pin' : 'setup')
    }

    const handleBack = () => {
        if (presetBarista) return 
        setPickedBarista(null)
        setPin('')
        setConfirmPin('')
        setError('')
        setStep('pick')
    }

    const handleVerify = async (e) => {
        e.preventDefault()
        if (!/^\d{4,6}$/.test(pin)) {
            setError('Enter your 4 to 6 digit PIN.')
            return
        }
        setSubmitting(true)
        setError('')

        const tryOfflineVerify = async () => {
            const cached = await offlineDB.baristaCache.get(pickedBarista.barista_id)
            if (!cached?.pin_hash) {
                setError('PIN not cached for offline use. Please reconnect once before going offline.')
                return false
            }
            const match = await bcrypt.compare(pin, cached.pin_hash)
            if (!match) { setError('Incorrect PIN.'); return false }

            if (isOnline && rotateCashDrawer) {
                await autoRotateCashDrawer({ branchId, outgoingBarista: selectedBarista, incomingBarista: pickedBarista })
            }
            setSelectedBarista(pickedBarista)
            onSuccess?.(pickedBarista)
            return true
        }

        if (!isOnline) {
            await tryOfflineVerify()
            setSubmitting(false)
            return
        }

        const result = await verifyBaristaPin(pickedBarista.barista_id, pin)

        if (result.needsSetup) { setSubmitting(false); setStep('setup'); return }

        if (!result.success) {
            // isOnline can be wrong (browser thinks it has a connection but there's
            // no real internet), so a failed online check falls back to the
            // offline cache before we treat this as a genuine failure.
            const offlineOk = await tryOfflineVerify()
            if (!offlineOk) setError(prev => prev || result.error || 'Incorrect PIN.')
            setSubmitting(false)
            return
        }

        setSubmitting(false)
        if (isOnline && rotateCashDrawer) {
            await autoRotateCashDrawer({ branchId, outgoingBarista: selectedBarista, incomingBarista: pickedBarista })
        }
        setSelectedBarista(pickedBarista)
        onSuccess?.(pickedBarista)
    }

    const handleSetup = async (e) => {
        e.preventDefault()
        if (!isOnline) {
            setError('You need to be online the first time you set up a PIN.')
            return
        }
        if (!/^\d{4,6}$/.test(pin)) { setError('PIN must be 4 to 6 digits.'); return }
        if (pin !== confirmPin) { setError('PIN confirmation does not match.'); return }

        setSubmitting(true)
        setError('')
        const result = await setupBaristaPin(pickedBarista.barista_id, pin)
        setSubmitting(false)

        if (!result.success) { setError(result.error || 'Failed to set up PIN.'); return }

        await Swal.fire({
            icon: 'success',
            title: 'PIN Created',
            text: `Your PIN has been set up, ${pickedBarista.first_name}.`,
            confirmButtonColor: '#FF6F3C'
        })

        if (isOnline && rotateCashDrawer) {
            await autoRotateCashDrawer({ branchId, outgoingBarista: selectedBarista, incomingBarista: pickedBarista })
        }
        setSelectedBarista(pickedBarista)
        onSuccess?.(pickedBarista)
    }

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">
                        {step === 'pick' && 'Who is working?'}
                        {step === 'pin' && `Welcome, ${pickedBarista?.first_name}`}
                        {step === 'setup' && `Set up your PIN, ${pickedBarista?.first_name}`}
                    </h3>
                    {allowCancel && (
                        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M6.4 19L5 17.6l5.6-5.6L5 6.4L6.4 5l5.6 5.6L17.6 5L19 6.4L13.4 12l5.6 5.6l-1.4 1.4l-5.6-5.6z"/>
                            </svg>
                        </button>
                    )}
                </div>

                <div className="p-6">
                    {step === 'pick' && (
                        baristasLoading ? (
                            <p className="text-center text-gray-400 text-sm py-6">Loading baristas...</p>
                        ) : branchBaristas.length === 0 ? (
                            <p className="text-center text-gray-400 text-sm py-6">No active baristas found for this branch.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                {branchBaristas.map((b) => (
                                    <button
                                        key={b.barista_id}
                                        onClick={() => handlePick(b)}
                                        className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:border-[#A0826D] hover:bg-orange-50 transition-colors"
                                    >
                                        <span className="w-10 h-10 rounded-full bg-[#C9B5A0] flex items-center justify-center text-[#303030] text-sm font-bold">
                                            {b.first_name[0]}{b.last_name[0]}
                                        </span>
                                        <span className="text-sm font-medium text-gray-800 text-center">{b.first_name} {b.last_name}</span>
                                    </button>
                                ))}
                            </div>
                        )
                    )}

                    {step === 'pin' && (
                        <form onSubmit={handleVerify} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Enter your PIN</label>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={6}
                                    autoFocus
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent text-center tracking-widest text-lg text-gray-900"
                                    placeholder="••••"

                                />
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            <div className="flex gap-3">
                                {!presetBarista && (
                                    <button type="button" onClick={handleBack} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors">
                                        Back
                                    </button>
                                )}
                                <button type="submit" disabled={submitting} className="flex-1 px-4 py-2.5 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors disabled:opacity-50">
                                    {submitting ? 'Verifying...' : 'Continue'}
                                </button>
                            </div>
                        </form>
                    )}

                    {step === 'setup' && (
                        <form onSubmit={handleSetup} className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-xs text-amber-800">No PIN found for this account yet. Create one now to continue.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">New PIN</label>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={6}
                                    autoFocus
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent text-gray-900"
                                    placeholder="4 to 6 digits"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm PIN</label>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={confirmPin}
                                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent text-gray-900"
                                    placeholder="Re-enter PIN"
                                />
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            <div className="flex gap-3">
                                {!presetBarista && (
                                    <button type="button" onClick={handleBack} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors">
                                        Back
                                    </button>
                                )}
                                <button type="submit" disabled={submitting} className="flex-1 px-4 py-2.5 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors disabled:opacity-50">
                                    {submitting ? 'Saving...' : 'Save & Continue'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}
